import type {
  AxiosRequestConfig,
  DefaultQueryError,
} from '@ahm/api-wrappers-core';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { apiRoutes } from './api-routes';
import { webviewHttpClient } from '@/app/[locale]/(m)/components/auth/webview-http-client';

export interface CancelOrderParams {
  order_code: string;
  cancel_code: string;
  comment?: string;
}

export interface CancelOrderResponse {
  success: boolean;
  message?: string;
}

export const cancelOrderFn = async (
  token: string,
  params: CancelOrderParams,
  config: AxiosRequestConfig = {}
): Promise<CancelOrderResponse> => {
  const response = await webviewHttpClient.delete<CancelOrderResponse>(
    apiRoutes.ahafood.store.cancel_order(params.order_code),
    {
      ...config,
      data: {
        cancel_code: params.cancel_code,
        comment: params.comment ?? '',
      },
      headers: {
        ...config.headers,
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

export const useCancelOrder = (
  token: string,
  options?: UseMutationOptions<
    CancelOrderResponse,
    DefaultQueryError,
    CancelOrderParams
  >
) =>
  useMutation<CancelOrderResponse, DefaultQueryError, CancelOrderParams>({
    mutationFn: (params) => cancelOrderFn(token, params),
    ...options,
  });

// Cancel reason codes based on the documentation
export const CANCEL_REASONS = {
  user_no_driver_accept: 'Driver no accept',
  user_user_wrong: 'User has mistaken',
  partner_merchant_doesnt_have_required_packages:
    "Merchant doesn't have required packages",
} as const;

export type CancelReasonCode = keyof typeof CANCEL_REASONS;
